import React, { useState, useEffect, useMemo, useRef } from 'react'
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getPaginationRowModel,
} from '@tanstack/react-table'

import TableHeader from '../shared/TableHeader'
import TableFooter from './TableFooter'
import TableBody from '../shared/TableBody'
import { convertFromParams } from '../../../../utils/table/sortBy'
import getAppliedFilterString from '../../../../utils/table/getAppliedFilterString'

const Table = ({
    name,
    columns,
    data,
    loading,
    pageCount: controlledPageCount,
    resultsCount,
    filterExpanded,
    filterFields,
    setParams,
    params,
    storedParams,
    refinedParams,
}) => {
    const initialPageIndex =
        params && params[name] ? Number(params[name].Page) - 1 : 0
    const initialPageSize =
        params && params[name] ? Number(params[name].PageSize) : 50
    const initialSorting =
        params && params[name]
            ? convertFromParams(
                  params[name].SortByField,
                  params[name].SortOrder
              )
            : []

    const [sorting, setSorting] = useState(initialSorting)
    const [pagination, setPagination] = useState({
        pageIndex: initialPageIndex,
        pageSize: initialPageSize,
    })

    const table = useReactTable({
        columns,
        data,
        state: {
            sorting,
            pagination,
            columnVisibility: { FacilityID: false },
        },
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        manualPagination: true,
        manualSorting: true,
        enableSortingRemoval: false,
        pageCount: controlledPageCount,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        sortDescFirst: false,
    })

    const { pageIndex, pageSize } = pagination

    useEffect(() => {
        const sortByProps = sorting.length
            ? {
                  SortByField: `${sorting[0].id}`,
                  SortOrder: sorting[0].desc ? 'DESC' : 'ASC',
              }
            : {}
        const newParams = {
            ...params[name],
            Page: pageIndex + 1,
            PageSize: pageSize,
            ...sortByProps,
        }
        setParams(newParams, name)
    }, [pageIndex, pageSize, sorting])

    const initialPage = useMemo(
        () => (params && params[name] ? params[name].Page : null),
        [params, name]
    )
    
    // useEffect(() => {
    //     if (initialPage) {
    //         table.setPageIndex(initialPage - 1)
    //     }
    // }, [initialPage])

    const relatedStoredParams = useMemo(
        () =>
            (storedParams &&
                storedParams
                    .filter((x) => Object.keys(x)[0] === name)
                    .map((x) => x[name])) ||
            [],
        [storedParams]
    )

    const relatedRefinedParams = useMemo(
        () =>
            (refinedParams &&
                refinedParams
                    .filter((x) => Object.keys(x)[0] === name)
                    .map((x) => x[name])) ||
            [],
        [refinedParams]
    )

    const pageCount = table.getPageCount()
    const canPreviousPage = table.getCanPreviousPage()
    const canNextPage = table.getCanNextPage()
    const pageOptions = Array.from({ length: pageCount }, (_, i) => i)

    return (
        <>
            <div
                className={`${
                    filterExpanded ? 'is-hidden' : 'is-visible'
                } filterSummaryWrapper`}
            >
                <div className="filterSummaryText">
                    {'Applied Filters: '}
                    {name === 'sample' &&
                    refinedParams &&
                    Array.isArray(refinedParams) &&
                    refinedParams.length ? (
                        <>
                            {'Stations with results for: '}
                            {[...relatedRefinedParams, params[name]]
                                .map((x) =>
                                    getAppliedFilterString(x, filterFields)
                                )
                                .filter((x) => x != null && x !== '')
                                .join(' and ')}
                        </>
                    ) : (
                        <>
                            {[...relatedStoredParams, params[name]]
                                .map((x) =>
                                    getAppliedFilterString(x, filterFields)
                                )
                                .filter((x) => x != null && x !== '')
                                .join(' | ')}
                        </>
                    )}
                </div>
            </div>
            <div
                className={`${
                    filterExpanded ? 'is-hidden' : 'is-visible'
                } explorerTableWrapper`}
            >
                <table className="table is-striped is-fullwidth is-hoverable explorerTable">
                    <TableHeader headerGroups={table.getHeaderGroups()} />
                    <TableBody
                        {...{
                            rows: table.getRowModel().rows,
                            loading,
                            controlledPageCount,
                            pageSize,
                        }}
                    />
                </table>
            </div>
            <TableFooter
                {...{
                    filterExpanded,
                    gotoPage: (n) => table.setPageIndex(n),
                    previousPage: () => table.previousPage(),
                    canPreviousPage,
                    nextPage: () => table.nextPage(),
                    canNextPage,
                    pageCount,
                    resultsCount,
                    pageOptions,
                    pageIndex,
                    pageSize,
                    setPageSize: (n) => table.setPageSize(n),
                    loading,
                }}
            />
        </>
    )
}

export default Table
